<script lang="ts">
  import { setProgressBarContext } from './progressBarContext'
  import videoStore from '../../../stores/video.svelte'
  import withPreventDefault from '../../../utils/withPreventDefault'

  type ClientXType = MouseEvent['clientX'] | Touch['clientX']

  type Props = {
    duration: number
    isLiveStream?: boolean
    seekTimeTransformer?: (arg: number) => number
    children?: () => any
  }
  let {
    duration,
    isLiveStream = false,
    seekTimeTransformer,
    children,
  }: Props = $props()

  let progressBarContextState = $state({
    hoverTime: 0,
    isDragging: false,
    isSmooth: true,
    isLiveStream,
    width: 0,
  })

  setProgressBarContext(progressBarContextState)

  let progressBarContainer: HTMLElement
  let clientX = $state(0)
  let wasPlayingBeforeDrag = $state(false)

  // UTIL FUNCTION

  const checkIsMobileEvent = (
    e: MouseEvent | TouchEvent,
    callback: () => void,
    isMobile: boolean,
  ) =>
    withPreventDefault(e, () => {
      if (videoStore.state.isMobileLayout === isMobile) {
        callback()
      }
    })

  // EVENTS COMMON HANDLERS

  const handleClickStart = (eventClientX: ClientXType) => {
    progressBarContextState.isDragging = true
    seek(eventClientX)
  }

  const handleClickMove = (
    eventClientX: ClientXType,
    updateHoverTime: boolean,
  ) => {
    const rect = progressBarContainer.getBoundingClientRect()
    if (eventClientX >= rect.left && eventClientX <= rect.right) {
      if (progressBarContextState.isDragging) {
        if (!wasPlayingBeforeDrag && videoStore.state.isPlaying) {
          wasPlayingBeforeDrag = true
          videoStore.state.videoController.pause()
        }
        seek(eventClientX)
      }
      if (updateHoverTime) {
        clientX = eventClientX
      }
    }
  }

  const handleClickEnd = () => {
    if (progressBarContextState.isDragging) {
      progressBarContextState.isDragging = false
      if (wasPlayingBeforeDrag && !videoStore.state.isPlaying) {
        videoStore.state.videoController.play()
      }
      wasPlayingBeforeDrag = false
    }
  }

  const handleMouseLeave = () => {
    progressBarContextState.hoverTime = 0
    clientX = 0
    progressBarContextState.isDragging = false
  }

  // DESKTOP ONLY EVENTS

  const onMouseDown = (e: MouseEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickStart(e.clientX)
      },
      false,
    )

  const onMouseMove = (e: MouseEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickMove(e.clientX, true)
      },
      false,
    )

  const onMouseUp = (e: MouseEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickEnd()
      },
      false,
    )

  const onMouseLeave = (e: MouseEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleMouseLeave()
      },
      false,
    )

  // MOBILE ONLY EVENTS

  const onTouchStart = (e: TouchEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickStart(e.touches[0].clientX)
      },
      true,
    )

  const onTouchMove = (e: TouchEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickMove(e.touches[0].clientX, false)
        videoStore.mouseMove()
      },
      true,
    )

  const onTouchEnd = (e: TouchEvent) =>
    checkIsMobileEvent(
      e,
      () => {
        handleClickEnd()
      },
      true,
    )

  // CONTROLS FUNCTIONS

  function seek(eventClientX: ClientXType) {
    const rect = progressBarContainer.getBoundingClientRect()
    const percent = (eventClientX - rect.left) / rect.width
    let time = percent * duration
    if (seekTimeTransformer) {
      time = seekTimeTransformer(time)
    }
    videoStore.state.currentTime = time
    videoStore.state.videoController?.setCurrentTime(time)
  }

  function updateHoverTime(clientX: ClientXType, duration: number) {
    const rect = progressBarContainer.getBoundingClientRect()
    const percent =
      (isLiveStream ? rect.right - clientX : clientX - rect.left) / rect.width
    progressBarContextState.hoverTime = Math.max(
      0,
      Math.min(percent * duration, duration),
    )
  }

  // EFFECTS

  $effect(() => {
    if (clientX !== 0) {
      updateHoverTime(clientX, duration)
    }
  })

  $effect(() => {
    if (!videoStore.state.isEnded && !progressBarContextState.isSmooth) {
      setTimeout(() => {
        progressBarContextState.isSmooth = true
      }, 300)
    }

    if (videoStore.state.isEnded) {
      progressBarContextState.isSmooth = false
    }
  })
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div class="px-4 mobile:px-3">
  <div
    class="relative w-full py-1 cursor-pointer group z-10 mobile:py-2"
    bind:this={progressBarContainer}
    onmousedown={onMouseDown}
    onmousemove={onMouseMove}
    onmouseup={onMouseUp}
    onmouseleave={onMouseLeave}
    ontouchmove={onTouchMove}
    ontouchstart={onTouchStart}
    ontouchend={onTouchEnd}
    bind:clientWidth={progressBarContextState.width}
  >
    <div class="relative h-[4px] w-full cursor-pointer mobile:h-[3px] z-10">
      {@render children?.()}
    </div>
    {#if progressBarContextState.isDragging}
      <div
        class="absolute -bottom-[40px] z-0 -left-4 mobile:left-0 mobile:bottom-0"
        style:width={`${videoStore.state.containerElement?.clientWidth}px`}
        style:height={`${videoStore.state.containerElement?.clientHeight}px`}
      ></div>{/if}
  </div>
</div>
